import { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import { Button } from '@/components/ui/Button';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { api } from '@/lib/api';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

export function LandingPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const questionInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [questionInput, setQuestionInput] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [typingExample, setTypingExample] = useState('');
  const [exampleIndex, setExampleIndex] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [heroTagline, setHeroTagline] = useState('');
  const navigate = useNavigate();
  const { user, isSignedIn } = useClerk();

  const heroTaglines = [
    "Technically not legal advice. Still might help.",
    "We're not lawyers. We're just really good at reading legalese.",
    "This is definitely not legal advice. But it might answer your question.",
    "Not legal advice. Not a lawyer. Still probably useful.",
    "We'll give you an answer we're legally not allowed to call advice.",
  ];

  useEffect(() => {
    let taglineIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeout: NodeJS.Timeout;

    const typeTagline = () => {
      const currentTagline = heroTaglines[taglineIndex];
      if (!isDeleting) {
        setHeroTagline(currentTagline.slice(0, charIndex + 1));
        charIndex++;
        if (charIndex === currentTagline.length) {
          timeout = setTimeout(() => {
            isDeleting = true;
            typeTagline();
          }, 3000);
          return;
        }
      } else {
        setHeroTagline(currentTagline.slice(0, charIndex - 1));
        charIndex--;
        if (charIndex === 0) {
          isDeleting = false;
          taglineIndex = (taglineIndex + 1) % heroTaglines.length;
        }
      }
      timeout = setTimeout(typeTagline, isDeleting ? 30 : 50);
    };

    timeout = setTimeout(typeTagline, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const exampleQuestions = [
    'Can my landlord keep my security deposit?',
    'Is it illegal to record phone calls?',
    'Can my boss read my work emails?',
    'Do I need a permit for a home renovation?',
  ];

  useEffect(() => {
    if (isInputFocused || questionInput) return;

    const currentExample = exampleQuestions[exampleIndex];
    let charIndex = 0;
    let isDeleting = false;
    let timeout: NodeJS.Timeout;

    const type = () => {
      if (!isDeleting) {
        setTypingExample(currentExample.slice(0, charIndex + 1));
        charIndex++;
        if (charIndex === currentExample.length) {
          timeout = setTimeout(() => {
            isDeleting = true;
            type();
          }, 2000);
          return;
        }
      } else {
        setTypingExample(currentExample.slice(0, charIndex - 1));
        charIndex--;
        if (charIndex === 0) {
          isDeleting = false;
          setExampleIndex((prev) => (prev + 1) % exampleQuestions.length);
          return;
        }
      }
      timeout = setTimeout(type, isDeleting ? 30 : 50);
    };

    timeout = setTimeout(type, 1000);
    return () => clearTimeout(timeout);
  }, [exampleIndex, isInputFocused, questionInput]);

  const handleAskQuestion = () => {
    const question = questionInput.trim();
    if (!question) return;
    localStorage.setItem('pendingQuestion', question);
    localStorage.setItem('pendingFiles', JSON.stringify(uploadedFiles));
    navigate('/sign-up');
  };

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setIsUploading(true);

    try {
      if (isSignedIn) {
        const uploaded = await Promise.all(
          fileArray.map(async (file) => {
            const doc = await api.uploadDocument(file);
            return {
              id: doc.id,
              name: file.name,
              size: file.size,
              type: file.type,
              preview: doc.url,
            };
          })
        );
        setUploadedFiles((prev) => [...prev, ...uploaded]);
      } else {
        const localFiles = fileArray.map((file) => ({
          id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: URL.createObjectURL(file),
        }));
        setUploadedFiles((prev) => [...prev, ...localFiles]);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isSignedIn]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const files: File[] = [];
    for (const item of items) {
      if (item.type.startsWith('image/') || item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      processFiles(files);
    }
  }, [processFiles]);

  const removeFile = (id: string) => {
    setUploadedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview && file.id.startsWith('local-')) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 font-sans overflow-x-hidden">
      {/* Schema.org WebSite Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'notlegal.chat',
            url: 'https://notlegal.chat',
            description: 'Free legal Q&A platform providing answers to common legal questions.',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://notlegal.chat/search?q={search_term_string}',
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />

      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-primary-900/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <NotLegalIcon name="gavel" className="w-8 h-8 text-amber-400" />
            <span className="text-xl font-bold text-white">notlegal.chat</span>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/pricing" className="text-sm font-medium text-primary-200 hover:text-white transition-colors">
              Pricing
            </Link>
            <Link to="/blog" className="text-sm font-medium text-primary-200 hover:text-white transition-colors">
              Blog
            </Link>
          </nav>
          <div className="flex items-center space-x-3">
            <Link to="/sign-in">
              <Button variant="ghost" size="sm" className="text-white hover:text-white hover:bg-white/10">Sign In</Button>
            </Link>
            <Link to="/sign-up">
              <Button size="sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* SEO Hero Section */}
      <section className="py-12 px-6 bg-surface-900">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Free Legal Answers to Your Questions
          </h1>
          <p className="text-lg text-primary-200 mb-8 max-w-2xl mx-auto">
            Get instant answers to common legal questions about employment, housing,
            family law, and more. General information to help you understand your rights.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Tenant Rights', slug: 'landlord-tenant-rights' },
              { label: 'Employment Law', slug: 'employee-rights' },
              { label: 'Divorce', slug: 'family-law-questions' },
              { label: 'Small Claims', slug: 'small-claims-court' },
              { label: 'Traffic Tickets', slug: 'traffic-violations' },
              { label: 'Contracts', slug: 'contract-questions' },
              { label: 'Debt Collection', slug: 'debt-collection-rights' },
              { label: 'Personal Injury', slug: 'personal-injury-basics' },
            ].map((topic) => (
              <Link
                key={topic.slug}
                to={`/legal/${topic.slug}`}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm text-white transition-colors"
              >
                {topic.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
        <div className="max-w-3xl mx-auto text-center w-full">
          <div className="mb-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              Ask your <span className="text-amber-400">legal question</span>
            </h2>
            <p className="text-xl text-primary-200 min-h-[2.5rem] flex items-center justify-center">
              <span className="typing-text">{heroTagline}</span>
            </p>
          </div>

          <div
            className={`relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border-2 transition-all duration-300 ${
              isInputFocused
                ? 'scale-[1.02] shadow-3xl border-white/40'
                : 'border-white/20'
            } ${isDragOver ? 'border-amber-400 bg-white/20' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isDragOver && (
              <div className="absolute inset-0 bg-amber-400/10 rounded-3xl flex items-center justify-center z-10 border-2 border-amber-400 border-dashed">
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-12 h-12 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-amber-400 font-semibold text-lg">Drop files here</span>
                </div>
              </div>
            )}

            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg border border-white/20"
                  >
                    <NotLegalIcon name="file" className="w-4 h-4 text-primary-300" />
                    <span className="text-sm text-white max-w-[120px] truncate">{file.name}</span>
                    <span className="text-xs text-primary-300">({formatFileSize(file.size)})</span>
                    <button
                      onClick={() => removeFile(file.id)}
                      className="text-primary-300 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <textarea
                ref={questionInputRef}
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAskQuestion();
                  }
                }}
                onPaste={handlePaste}
                placeholder={typingExample || "e.g., Can my landlord keep my security deposit for 'normal wear and tear'?"}
                className="w-full px-5 py-5 bg-white border-2 border-white/20 rounded-2xl text-surface-900 placeholder:text-surface-400 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all resize-none text-lg"
                rows={3}
              />

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.json,.yaml,.yml,.csv"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />

              <div className="flex items-center justify-between pt-3 mt-1">
                <button
                  onClick={openFilePicker}
                  className="flex items-center gap-2 text-primary-300 hover:text-white transition-colors"
                  title="Attach files"
                >
                  {isUploading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  )}
                  <span className="text-sm">Attach files</span>
                </button>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-primary-300 hidden sm:inline">
                    Press Enter to submit
                  </span>
                  <Button
                    size="lg"
                    variant="secondary"
                    icon={<NotLegalIcon name="sparkle" className="w-5 h-5" />}
                    onClick={handleAskQuestion}
                    className="hover:scale-105 transition-transform"
                  >
                    Ask Away
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {[
              'Can I fire my employee for being annoying?',
              'Is it illegal to lie on my resume?',
              'Can my neighbor trim my tree branches?',
              'Do I have to tip at restaurants?',
            ].map((q, i) => (
              <button
                key={i}
                onClick={() => setQuestionInput(q)}
                className="interactive px-5 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white text-sm transition-all hover:scale-105 hover:shadow-lg"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-b from-white to-surface-50" />
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-amber-200/10 rounded-full blur-3xl" />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              It's surprisingly simple
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-surface-900 mb-4">
              How This Works
            </h2>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto">
              Three steps. No appointments. No bills. Just answers.
            </p>
          </div>

          {/* Step cards with connecting line */}
          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-0.5 bg-gradient-to-r from-primary-300 via-primary-400 to-primary-300" />

            <div className="grid md:grid-cols-3 gap-6">
              {/* Step 1 */}
              <div className="group relative">
                <div className="relative bg-white rounded-2xl p-8 shadow-lg shadow-surface-200/50 border border-surface-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-primary-200/30 hover:-translate-y-2">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Icon */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-200 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    {/* Step number badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-primary-100">
                      <span className="text-primary-600 font-bold text-sm">1</span>
                    </div>
                  </div>

                  <div className="relative">
                    <h3 className="text-xl font-bold text-surface-900 mb-3 group-hover:text-primary-700 transition-colors">
                      Ask Anything
                    </h3>
                    <p className="text-surface-600 leading-relaxed">
                      Type your legal question. Attach documents if you've got them. Don't worry about sounding smart — we speak human.
                    </p>
                  </div>

                  {/* Decorative elements */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="group relative mt-0 md:mt-8">
                <div className="relative bg-white rounded-2xl p-8 shadow-lg shadow-surface-200/50 border border-surface-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-primary-200/30 hover:-translate-y-2">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-200 mx-auto group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-amber-100">
                      <span className="text-amber-600 font-bold text-sm">2</span>
                    </div>
                  </div>

                  <div className="relative">
                    <h3 className="text-xl font-bold text-surface-900 mb-3 group-hover:text-amber-700 transition-colors">
                      Get Your Answer
                    </h3>
                    <p className="text-surface-600 leading-relaxed">
                      Instant insights. Clear explanations. We break down complex legal concepts into plain English you can actually use.
                    </p>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </div>

              {/* Step 3 */}
              <div className="group relative">
                <div className="relative bg-white rounded-2xl p-8 shadow-lg shadow-surface-200/50 border border-surface-100 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-primary-200/30 hover:-translate-y-2">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-200 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-emerald-100">
                      <span className="text-emerald-600 font-bold text-sm">3</span>
                    </div>
                  </div>

                  <div className="relative">
                    <h3 className="text-xl font-bold text-surface-900 mb-3 group-hover:text-emerald-700 transition-colors">
                      Know Your Rights
                    </h3>
                    <p className="text-surface-600 leading-relaxed">
                      Walk away with clarity. Understand where you stand and what options are actually available to you.
                    </p>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </div>
            </div>
          </div>

          {/* Trust indicator */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-surface-100 rounded-full">
              <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-surface-700 font-medium">Free to try. No lawyers. No judgment.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Legal Topics Grid */}
      <section className="py-16 px-6 bg-surface-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4 text-surface-900">
            Explore Legal Topics
          </h2>
          <p className="text-center text-surface-600 mb-12">
            Browse our comprehensive guides on common legal questions
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { title: 'Landlord & Tenant', icon: '🏠', url: '/legal/landlord-tenant-rights', desc: 'Rights, deposits, eviction' },
              { title: 'Employment Law', icon: '💼', url: '/legal/employee-rights', desc: 'Wrongful termination, wages' },
              { title: 'Family Law', icon: '👨‍👩‍👧', url: '/legal/family-law-questions', desc: 'Divorce, custody, support' },
              { title: 'Traffic Violations', icon: '🚗', url: '/legal/traffic-violations', desc: 'Tickets, DUI, license' },
              { title: 'Small Claims', icon: '⚖️', url: '/legal/small-claims-court', desc: 'How to sue, limits' },
              { title: 'Personal Injury', icon: '🏥', url: '/legal/personal-injury-basics', desc: 'Injury claims, negligence' },
              { title: 'Debt & Collections', icon: '💳', url: '/legal/debt-collection-rights', desc: 'Harassment, statutes' },
              { title: 'Contracts', icon: '📝', url: '/legal/contract-questions', desc: 'Breach, verbal vs written' },
            ].map((topic) => (
              <Link
                key={topic.title}
                to={topic.url}
                className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow group block"
              >
                <div className="text-3xl mb-3">{topic.icon}</div>
                <h3 className="font-semibold text-surface-900 group-hover:text-primary-600">
                  {topic.title}
                </h3>
                <p className="text-sm text-surface-500 mt-1">{topic.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Common Questions FAQ Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4 text-surface-900">
            Common Legal Questions
          </h2>
          <p className="text-center text-surface-600 mb-12">
            Quick answers to frequently asked legal questions
          </p>

          <div className="space-y-4">
            {[
              {
                question: 'Can my landlord keep my security deposit?',
                answer: 'Landlords can only deduct from your security deposit for unpaid rent, damage beyond normal wear and tear, or cleaning costs necessary to return the unit to its original condition. Normal wear and tear, pre-existing damage, and ordinary cleaning cannot be deducted.',
              },
              {
                question: 'Can my boss fire me for no reason?',
                answer: 'In most states, yes - under "at-will employment," employers can fire employees for any reason or no reason. However, they cannot fire you for discriminatory reasons, in retaliation for protected complaints, or in violation of an employment contract.',
              },
              {
                question: 'How do I sue someone in small claims court?',
                answer: 'To sue in small claims court: (1) Gather evidence of your claim, (2) Fill out the appropriate forms from your local court, (3) Pay the filing fee, (4) Serve the defendant with the papers, (5) Attend your hearing and present your case.',
              },
              {
                question: 'What should I do if I get a speeding ticket?',
                answer: 'You generally have three options: (1) Pay the ticket and accept points on your license, (2) Contest the ticket in court, or (3) Take traffic school (if eligible) to keep points off your record. Consider the impact on your insurance rates before deciding.',
              },
            ].map((faq, index) => (
              <details key={index} className="bg-surface-50 rounded-xl overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer font-medium text-surface-900 list-none flex items-center justify-between">
                  {faq.question}
                  <svg className="w-5 h-5 text-surface-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-4 text-surface-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 px-6 bg-surface-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-surface-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto">
              No hidden fees. No fine print. No actual legal liability.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-surface-200 hover:-translate-y-2">
              <h3 className="text-xl font-bold text-surface-900 mb-2">Clown Price</h3>
              <div className="mb-6">
                <span className="text-4xl font-black text-surface-900">$0</span>
                <span className="text-surface-500">/month</span>
              </div>
              <p className="text-surface-600 mb-6">Perfect for completely irresponsible individuals.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  5 legal "opinions" per month
                </li>
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Basic document "review"
                </li>
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Community support
                </li>
              </ul>
              <Link to="/sign-up" className="block w-full">
                <Button variant="ghost" className="w-full border border-surface-200 hover:scale-105 transition-transform">Get Started</Button>
              </Link>
            </div>

            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 shadow-xl transform md:-translate-y-4 border border-primary-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-6">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg">MOST POPULAR</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 mt-2">Not Quite A Lawyer</h3>
              <div className="mb-6">
                <span className="text-4xl font-black text-white">$29</span>
                <span className="text-primary-200">/month</span>
              </div>
              <p className="text-primary-100 mb-6">For those who want to pretend they're serious.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-300 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Unlimited "opinions"
                </li>
                <li className="flex items-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-300 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Advanced document analysis
                </li>
                <li className="flex items-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-300 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Contract "review"
                </li>
                <li className="flex items-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-300 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Priority support
                </li>
              </ul>
              <Link to="/sign-up" className="block w-full">
                <Button variant="secondary" size="lg" className="w-full hover:scale-105 transition-transform">Get Started</Button>
              </Link>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-surface-200 hover:-translate-y-2">
              <h3 className="text-xl font-bold text-surface-900 mb-2">Actually A Lawyer?</h3>
              <div className="mb-6">
                <span className="text-4xl font-black text-surface-900">$99</span>
                <span className="text-surface-500">/month</span>
              </div>
              <p className="text-surface-600 mb-6">For firms that love liability.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Everything in Pro
                </li>
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Team collaboration
                </li>
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  API access
                </li>
                <li className="flex items-center text-surface-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Dedicated support
                </li>
              </ul>
              <Link to="/sign-up" className="block w-full">
                <Button variant="ghost" className="w-full border border-surface-200 hover:scale-105 transition-transform">Contact Sales</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-surface-900 py-12 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <NotLegalIcon name="gavel" className="w-8 h-8 text-primary-500" />
                <span className="text-xl font-bold text-white">notlegal.chat</span>
              </div>
              <p className="text-surface-400 max-w-sm">
                The internet's premier destination for legally questionable AI-generated "advice." Not liable for anything, ever.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal Topics</h4>
              <ul className="space-y-2">
                <li><Link to="/legal/landlord-tenant-rights" className="text-surface-400 hover:text-primary-400 transition-colors">Tenant Rights</Link></li>
                <li><Link to="/legal/employee-rights" className="text-surface-400 hover:text-primary-400 transition-colors">Employee Rights</Link></li>
                <li><Link to="/legal/family-law-questions" className="text-surface-400 hover:text-primary-400 transition-colors">Family Law</Link></li>
                <li><Link to="/legal/small-claims-court" className="text-surface-400 hover:text-primary-400 transition-colors">Small Claims</Link></li>
                <li><Link to="/legal/traffic-violations" className="text-surface-400 hover:text-primary-400 transition-colors">Traffic Tickets</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/blog" className="text-surface-400 hover:text-primary-400 transition-colors">Legal Blog</Link></li>
                <li><Link to="/pricing" className="text-surface-400 hover:text-primary-400 transition-colors">Pricing</Link></li>
                <li><Link to="/disclaimer" className="text-surface-400 hover:text-primary-400 transition-colors">Disclaimer</Link></li>
                <li><a href="#" className="text-surface-400 hover:text-primary-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-surface-400 hover:text-primary-400 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-surface-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-surface-500 text-sm">
              &copy; 2026 notlegal.chat. Not a real company. Don't sue us.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="#" className="text-surface-500 hover:text-primary-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="text-surface-500 hover:text-primary-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
